import { ImpersonationContextKey, ModalChildComponentContextKey } from '@AppModels/.';
import * as SharedAppState from '@AppModels/AppState';
import { EventLogger } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { action, autorun, IReactionDisposer, makeObservable, observable } from 'mobx';
import { observer, Provider } from 'mobx-react';
import * as React from 'react';
import { CredentialStore } from '../../../api/CredentialStore';
import {
	FullScreenModalViewModel,
	IUserSessionContextConfig,
	UserSessionContext,
} from '../../../viewmodels/AppViewModels';
import { BrowserPushNotificationsViewModel } from '../../../viewmodels/PushNotifications';
import { RegEdLeadTimeOverrideHost } from '../../components/compliance/RegEdLeadTimeOverrideHost';
import { SonnerToast } from '../../components/SonnerToast';
import { TooltipProvider } from '../../components/Tooltip';
import Benny from '../../fonts/Benny.ttf';
import Erin from '../../fonts/Erin.ttf';
import John from '../../fonts/John.ttf';
import Moss from '../../fonts/Moss.ttf';
import { NuqsRR5Adapter } from '../../NuqsAdapter';
import { LevReactQueryDevtools } from '../../ReactQueryDevtools';
import { ReactQueryProvider } from '../../ReactQueryProvider';
import { FocusedViewProvider } from '../Contact/Focused/FocusedViewProvider';
import { ManagedAccountAccessSessionBoundary } from '../../components/ManagedAccountAccessSessionBoundary';
import { HIPAATermsAcknowledgementProvider } from '../HIPAATermsAcknowledgement/HIPAATermsAcknowledgementProvider';
import { TextingCampaignRegistrationContextProvider } from '../Texting/TextingCampaignRegistrationPrompt/context';
import { styleSheet } from './styles';

interface IProps {
	userSessionConfig?: IUserSessionContextConfig;
}

interface IState {
	isLoading?: boolean;
}

const loadFont = (family: string, url: string) => {
	const face = new FontFace(family, `url(${url})`, { style: 'normal', weight: 'normal' });
	face.load().catch(() => {
		/* font preload failed; not actionable */
	});
	return face;
};

class _App extends React.Component<React.PropsWithChildren<IProps>, IState> {
	private userSession: UserSessionContext;
	private mMounted: boolean;
	private mLoggedInDisposer: IReactionDisposer;
	public readonly state: IState = {
		isLoading: true,
	};

	constructor(props: IProps) {
		super(props);

		makeObservable<_App, 'mMounted'>(this, {
			mMounted: observable,
			init: action,
		});

		const userSessionConfig: IUserSessionContextConfig = {
			...props.userSessionConfig,
			apiConfig: {
				...(
					props.userSessionConfig || {
						apiConfig: {
							baseUrl: import.meta.env.VITE_API_URL,
						},
					}
				).apiConfig,
				cookieSession: import.meta.env.VITE_COOKIE_SESSION_WEB === 'true',
				credentialStore: new CredentialStore(),
			},
		};

		this.userSession = new UserSessionContext(userSessionConfig);
		SharedAppState.AppState[SharedAppState.UserSessionViewModelKey] = this.userSession;

		SharedAppState.AppState[SharedAppState.ActionItemComposerViewModelKey].setUserSession(this.userSession);

		SharedAppState.AppState[SharedAppState.NoteComposerViewModelKey].setUserSession(this.userSession);

		SharedAppState.AppState[SharedAppState.QuickAddEntityViewModelKey].setUserSession(this.userSession);

		SharedAppState.AppState[SharedAppState.SingleEmailComposerKey].setUserSession(this.userSession);
		SharedAppState.AppState[SharedAppState.TextMessagingViewModelKey] = new Api.TextMessagingViewModel(
			this.userSession
		);
		SharedAppState.AppState[SharedAppState.FullScreenModalViewModelKey] = new FullScreenModalViewModel();
		SharedAppState.AppState[SharedAppState.PushNotificationsViewModelKey] = new BrowserPushNotificationsViewModel(
			this.userSession,
			{
				addLogger: Api.VmUtils.Noop,
				logEvent: (args, context) => console.log(args, context),
				removeLogger: Api.VmUtils.Noop,
			}
		);
	}

	init() {
		this.mMounted = true;

		import('../../../models/SessionReplayLogger')
			?.then(mod => {
				const SessionReplayLogger = mod.default as new (env: string, appId: string) => Api.ILogger<Api.ILogEvent>;

				const sessionReplayLogger = new SessionReplayLogger(
					import.meta.env.MODE,
					import.meta.env.VITE_SESSION_REPLAY_APPID
				);
				Api.EventLogger.addLogger(sessionReplayLogger);
			})
			.catch(() => {
				console.error('Failed to load Session Replay');
			});
		loadFont('Aladdin', Moss);
		loadFont('Cinderella', Erin);
		loadFont('Donald', Benny);
		loadFont('Nemo', Erin);
		loadFont('Scar', Erin);
		loadFont('Tarzan', John);
		loadFont('Woody', Benny);

		this.mLoggedInDisposer = autorun(() => {
			if (!!this.mMounted && !!this.userSession?.isAuthenticated) {
				SharedAppState.AppState[SharedAppState.TextMessagingViewModelKey].init(EventLogger);
			}
		});
		const promise = this.userSession.load();
		const onFinish = () => {
			if (this.mMounted) {
				this.setState({
					isLoading: false,
				});
			}
		};
		if (promise) {
			promise.then(onFinish).catch(onFinish);
		} else {
			onFinish();
		}
	}

	public componentDidMount() {
		this.init();
	}

	public componentWillUnmount() {
		this.mMounted = false;
		this.mLoggedInDisposer?.();

		this.mLoggedInDisposer = null;
	}

	public render() {
		return (
			<TooltipProvider>
				<ReactQueryProvider>
					<Provider
						{...SharedAppState.AppState}
						{...{ [ModalChildComponentContextKey]: null }}
						{...{ [ImpersonationContextKey]: null }}
					>
						<NuqsRR5Adapter>
							<HIPAATermsAcknowledgementProvider>
								<TextingCampaignRegistrationContextProvider>
									<FocusedViewProvider>
										<ManagedAccountAccessSessionBoundary />
										<div className={`${css(styleSheet.container)} root`}>
											{this.state.isLoading ? <span style={{ display: 'none' }} /> : this.props.children}
										</div>
									</FocusedViewProvider>
								</TextingCampaignRegistrationContextProvider>
							</HIPAATermsAcknowledgementProvider>
						</NuqsRR5Adapter>
					</Provider>
					<LevReactQueryDevtools />
				</ReactQueryProvider>
				<SonnerToast />
				<RegEdLeadTimeOverrideHost />
			</TooltipProvider>
		);
	}
}

export const App = observer(_App);
